// StaffDashboard.jsx - Fixed responsive tabs
import React, { useState, useEffect, useRef } from 'react';

import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Chart } from 'primereact/chart';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Badge } from 'primereact/badge';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Menu } from 'primereact/menu';
import { Timeline } from 'primereact/timeline';
import { Sidebar } from 'primereact/sidebar';
import useAuth from 'hooks/useAuth';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000/api';

const StaffDashboard = () => {

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [stats, setStats] = useState({
    totalOrdersToday: 0,
    pendingPickups: 0,
    pendingDeliveries: 0,
    completedToday: 0,
    revenueToday: 0,
    activeStaff: 0,
    urgentOrders: 0,
    avgCompletionTime: 0
  });
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState({
    orders: false,
    stats: false,
    chart: false
  });
  const [notifications, setNotifications] = useState([]);
  const [orderDetails, setOrderDetails] = useState(null);
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [liveLocations, setLiveLocations] = useState([]);
  const [mobileView, setMobileView] = useState(window.innerWidth < 768);
  const [filterSidebarVisible, setFilterSidebarVisible] = useState(false);
  const [staffId, setStaffId] = useState(null);
  const [error, setError] = useState(null);
  const [isStaffLoaded, setIsStaffLoaded] = useState(false);
  
  const toast = useRef(null);
  const menu = useRef(null);
  const auth = useAuth();
  
  // Fetch Staff ID on component mount
  useEffect(() => {
    fetchStaffId();
  }, []);

  const fetchStaffId = async () => {
    try {
      setLoading(prev => ({ ...prev, stats: true }));
      
   const response = await fetch(`${API_BASE_URL}dashboard/staffid?userid=${auth.userId}`, {
  method: 'GET',
  headers: {
   // 'Authorization': `Bearer ${auth.accessToken}`,
    'Content-Type': 'application/json',
  },

  //credentials: 'include' // Include if using cookies/session
});

      
      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Parse the JSON response
      const data = await response.json();
      
      // Handle response structure
      let staffIdValue = null;
      if (Array.isArray(data) && data.length > 0) {
        staffIdValue = data[0]?.staffid || null;
      } else if (data?.staffid) {
        staffIdValue = data.staffid;
      } else if (typeof data === 'number') {
        staffIdValue = data;
      }
      
      setStaffId(staffIdValue);
      setIsStaffLoaded(true);
      setError(null);
      
      // Load initial data only after staff ID is fetched
      if (staffIdValue) {
        loadInitialData(staffIdValue);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch staff ID:', err);
      showToast('error', 'Error', 'Failed to load staff information');
      setIsStaffLoaded(true);
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  };

  // Check screen size for responsive adjustments
  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const statusOptions = [
    { label: 'All Status', value: null },
    { label: 'Pending', value: 'Pending' },
    { label: 'Confirmed', value: 'Confirmed' },
    { label: 'Assigned', value: 'Assigned' },
    { label: 'Picked Up', value: 'PickedUp' },
    { label: 'Processing', value: 'Processing' },
    { label: 'Ready for Delivery', value: 'ReadyForDelivery' },
    { label: 'Out for Delivery', value: 'OutForDelivery' },
    { label: 'Delivered', value: 'Delivered' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  const statusSeverity = {
    'Pending': 'warning',
    'Confirmed': 'info',
    'Assigned': 'info',
    'PickedUp': 'success',
    'Processing': 'warning',
    'ReadyForDelivery': 'success',
    'OutForDelivery': 'warning',
    'Delivered': 'success',
    'Completed': 'success',
    'Cancelled': 'danger'
  };

  const prioritySeverity = {
    '1': 'success',
    '2': 'info',
    '3': 'warning',
    '4': 'danger'
  };

  // API Service Functions
  const fetchDashboardStats = async (staffIdValue) => {
    try {
      setLoading(prev => ({ ...prev, stats: true }));
      const response = await fetch(`${API_BASE_URL}dashboard/stats?staffId=${staffIdValue}`);
      if (!response.ok) throw new Error('Failed to fetch dashboard stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      showToast('error', 'Error', 'Failed to load dashboard statistics');
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  };

  const fetchOrders = async (filters = {}) => {
    if (!staffId) return;
    
    try {
      setLoading(prev => ({ ...prev, orders: true }));
      
      const params = new URLSearchParams({
        staffId: staffId,
        ...(filters.status && { status: filters.status }),
        ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
        ...(filters.endDate && { endDate: filters.endDate.toISOString() }),
        ...(filters.searchTerm && { searchTerm: filters.searchTerm }),
        ...(filters.isUrgent !== undefined && { isUrgent: filters.isUrgent }),
        ...(filters.priority && { priority: filters.priority })
      });

      const response = await fetch(`${API_BASE_URL}dashboard/orders?${params}`);
      if (!response.ok) throw new Error('Failed to fetch orders');
      
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast('error', 'Error', 'Failed to load orders');
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/order/${orderId}`);
      if (!response.ok) throw new Error('Failed to fetch order details');
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      showToast('error', 'Error', 'Failed to load order details');
      return null;
    }
  };

  const fetchWeeklyPerformance = async (staffIdValue) => {
    try {
      setLoading(prev => ({ ...prev, chart: true }));
      const response = await fetch(`${API_BASE_URL}dashboard/weekly-performance?staffId=${staffIdValue}`);
      if (!response.ok) throw new Error('Failed to fetch performance data');
      
      const data = await response.json();
      initChart(data);
    } catch (error) {
      console.error('Error fetching performance:', error);
      showToast('error', 'Error', 'Failed to load performance data');
    } finally {
      setLoading(prev => ({ ...prev, chart: false }));
    }
  };

  const fetchUrgentOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/urgent-orders`);
      if (!response.ok) throw new Error('Failed to fetch urgent orders');
      
      const data = await response.json();
      setUrgentOrders(data);
    } catch (error) {
      console.error('Error fetching urgent orders:', error);
    }
  };

  const fetchNotifications = async (staffIdValue) => {
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/staff/${staffIdValue}/notifications`);
      if (!response.ok) throw new Error('Failed to fetch notifications');
      
      const data = await response.json();
      setNotifications(data.filter(n => !n.isRead));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchLiveLocations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/live-locations`);
      if (!response.ok) throw new Error('Failed to fetch live locations');
      
      const data = await response.json();
      setLiveLocations(data);
    } catch (error) {
      console.error('Error fetching live locations:', error);
    }
  };

  const updateOrderStatus = async (bookingId, newStatus, notes = '') => {
    if (!staffId) return false;
    
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/order/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId,
          newStatus,
          staffId: staffId,
          notes
        })
      });

      if (!response.ok) throw new Error('Failed to update order status');
      
      const result = await response.json();
      showToast('success', 'Success', 'Order status updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating order status:', error);
      showToast('error', 'Error', 'Failed to update order status');
      return false;
    }
  };

  const startShift = async () => {
    if (!staffId) return false;
    
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/staff/${staffId}/start-shift`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to start shift');
      
      showToast('success', 'Success', 'Shift started successfully');
      return true;
    } catch (error) {
      console.error('Error starting shift:', error);
      showToast('error', 'Error', 'Failed to start shift');
      return false;
    }
  };

  const endShift = async () => {
    if (!staffId) return false;
    
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/staff/${staffId}/end-shift`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to end shift');
      
      showToast('success', 'Success', 'Shift ended successfully');
      return true;
    } catch (error) {
      console.error('Error ending shift:', error);
      showToast('error', 'Error', 'Failed to end shift');
      return false;
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}dashboard/notification/${notificationId}/mark-read`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to mark notification as read');
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  };

  // Initialize data after staff ID is loaded
  const loadInitialData = async (staffIdValue) => {
    if (!staffIdValue) return;
    
    await Promise.all([
      fetchDashboardStats(staffIdValue),
      fetchOrders(),
      fetchWeeklyPerformance(staffIdValue),
      fetchUrgentOrders(),
      fetchNotifications(staffIdValue),
      fetchLiveLocations()
    ]);
  };

  // Set up auto-refresh interval
  useEffect(() => {
    if (!staffId) return;
    
    const interval = setInterval(() => {
      fetchDashboardStats(staffId);
      fetchUrgentOrders();
      fetchLiveLocations();
    }, 30000);

    return () => clearInterval(interval);
  }, [staffId]);

  const initChart = (performanceData) => {
    const documentStyle = getComputedStyle(document.documentElement);
    
    const data = {
      labels: performanceData.map(p => p.day),
      datasets: [
        {
          label: 'Pickups',
          data: performanceData.map(p => p.pickups),
          fill: true,
          backgroundColor: documentStyle.getPropertyValue('--blue-100'),
          borderColor: documentStyle.getPropertyValue('--blue-500'),
          tension: 0.4
        },
        {
          label: 'Deliveries',
          data: performanceData.map(p => p.deliveries),
          fill: true,
          backgroundColor: documentStyle.getPropertyValue('--green-100'),
          borderColor: documentStyle.getPropertyValue('--green-500'),
          tension: 0.4
        },
        {
          label: 'Revenue',
          data: performanceData.map(p => p.revenue),
          fill: true,
          backgroundColor: documentStyle.getPropertyValue('--purple-100'),
          borderColor: documentStyle.getPropertyValue('--purple-500'),
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    };
    
    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          labels: {
            color: documentStyle.getPropertyValue('--text-color')
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: documentStyle.getPropertyValue('--text-color-secondary')
          },
          grid: {
            color: documentStyle.getPropertyValue('--surface-border')
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          ticks: {
            color: documentStyle.getPropertyValue('--text-color-secondary')
          },
          grid: {
            color: documentStyle.getPropertyValue('--surface-border')
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          ticks: {
            color: documentStyle.getPropertyValue('--text-color-secondary')
          },
          grid: {
            drawOnChartArea: false,
          },
        }
      }
    };

    setChartData(data);
    setChartOptions(options);
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag 
        value={rowData.status} 
        severity={statusSeverity[rowData.status] || 'info'} 
        rounded 
      />
    );
  };

  const priorityBodyTemplate = (rowData) => {
    const priorityLabel = rowData.priority || 'Normal';
    return (
      <Tag 
        value={priorityLabel} 
        severity={prioritySeverity[rowData.priorityLevel] || 'info'} 
      />
    );
  };

  const urgentBodyTemplate = (rowData) => {
    return rowData.isUrgent ? (
      <Tag value="URGENT" severity="danger" />
    ) : (
      <span>-</span>
    );
  };

  const actionBodyTemplate = (rowData) => {
    const nextStatus = getNextStatus(rowData.status);
    
    return (
      <div className="flex gap-2">
        <Button 
          icon="pi pi-eye" 
          className="p-button-rounded p-button-info p-button-text"
          onClick={() => openOrderDetails(rowData)}
          tooltip="View Details"
          tooltipOptions={{ position: 'top' }}
        />
        {nextStatus && (
          <Button 
            icon="pi pi-check" 
            className="p-button-rounded p-button-success p-button-text"
            onClick={() => handleStatusUpdate(rowData.id, nextStatus)}
            tooltip={`Mark as ${nextStatus}`}
            tooltipOptions={{ position: 'top' }}
          />
        )}
        <Button 
          icon="pi pi-directions" 
          className="p-button-rounded p-button-help p-button-text"
          tooltip="Get Directions"
          tooltipOptions={{ position: 'top' }}
          onClick={() => window.open(`https://maps.google.com/?q=${rowData.address}`, '_blank')}
        />
      </div>
    );
  };

  // Mobile card view for orders
  const mobileOrderCard = (order) => (
    <Card key={order.id} className="mb-3 border-1 surface-border">
      <div className="flex justify-content-between align-items-start mb-2">
        <div>
          <span className="font-bold text-lg">{order.bookingReference}</span>
          <div className="text-sm text-gray-600">{order.customerName}</div>
        </div>
        {statusBodyTemplate(order)}
      </div>
      <div className="mb-2">
        <div className="text-sm"><i className="pi pi-map-marker mr-2"></i>{order.address}</div>
        <div className="text-sm"><i className="pi pi-clock mr-2"></i>{order.pickupTime}</div>
      </div>
      <div className="flex justify-content-between align-items-center">
        <div className="flex gap-2">
          {priorityBodyTemplate(order)}
          {urgentBodyTemplate(order)}
        </div>
        <div className="flex gap-2">
          <Button 
            icon="pi pi-eye" 
            className="p-button-rounded p-button-info p-button-text"
            onClick={() => openOrderDetails(order)}
          />
          {getNextStatus(order.status) && (
            <Button 
              icon="pi pi-check" 
              className="p-button-rounded p-button-success p-button-text"
              onClick={() => handleStatusUpdate(order.id, getNextStatus(order.status))}
            />
          )}
          <Button 
            icon="pi pi-directions" 
            className="p-button-rounded p-button-help p-button-text"
            onClick={() => window.open(`https://maps.google.com/?q=${order.address}`, '_blank')}
          />
        </div>
      </div>
    </Card>
  );

  const openOrderDetails = async (order) => {
    const details = await fetchOrderDetails(order.id);
    if (details) {
      setOrderDetails(details);
      setOrderDialog(true);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    const success = await updateOrderStatus(orderId, newStatus);
    if (success) {
      await Promise.all([
        fetchDashboardStats(staffId),
        fetchOrders(),
        fetchUrgentOrders()
      ]);
    }
  };

  const handleStartShift = async () => {
    const success = await startShift();
    if (success) {
      await fetchDashboardStats(staffId);
      await fetchLiveLocations();
    }
  };

  const handleEndShift = async () => {
    const success = await endShift();
    if (success) {
      await fetchDashboardStats(staffId);
      await fetchLiveLocations();
    }
  };

  const handleNotificationClick = (notification) => {
    markNotificationAsRead(notification.id);
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'Pending': 'Confirmed',
      'Confirmed': 'Assigned',
      'Assigned': 'PickedUp',
      'PickedUp': 'Processing',
      'Processing': 'ReadyForDelivery',
      'ReadyForDelivery': 'OutForDelivery',
      'OutForDelivery': 'Delivered',
      'Delivered': 'Completed'
    };
    return statusFlow[currentStatus];
  };

  // Apply filters
  const applyFilters = () => {
    fetchOrders({
      status: statusFilter,
      startDate: dateRange?.[0],
      endDate: dateRange?.[1],
      searchTerm: searchTerm
    });
    setFilterSidebarVisible(false);
  };

  // Clear filters
  const clearFilters = () => {
    setStatusFilter(null);
    setDateRange([new Date(), new Date()]);
    setSearchTerm('');
    setTimeout(() => {
      fetchOrders({});
    }, 100);
    setFilterSidebarVisible(false);
  };

  // Debounced search
  useEffect(() => {
    if (!mobileView && staffId) {
      const timer = setTimeout(() => {
        fetchOrders({
          status: statusFilter,
          startDate: dateRange?.[0],
          endDate: dateRange?.[1],
          searchTerm: searchTerm
        });
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [searchTerm, statusFilter, dateRange, mobileView, staffId]);

  const showToast = (severity, summary, detail) => {
    if (toast.current) {
      toast.current.show({ severity, summary, detail, life: 3000 });
    }
  };

  // Loading state
  if (!isStaffLoaded) {
    return (
      <div className="flex justify-content-center align-items-center min-h-screen">
        <div className="text-center">
          <i className="pi pi-spin pi-spinner text-4xl text-blue-500 mb-3"></i>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !staffId) {
    return (
      <div className="flex justify-content-center align-items-center min-h-screen">
        <Card className="text-center">
          <i className="pi pi-exclamation-triangle text-4xl text-red-500 mb-3"></i>
          <h3 className="text-xl font-bold text-red-600">Error Loading Dashboard</h3>
          <p className="text-gray-600">{error}</p>
          <Button label="Retry" icon="pi pi-refresh" onClick={fetchStaffId} className="mt-3" />
        </Card>
      </div>
    );
  }

  // Mobile header with filter button
  const mobileHeader = (
    <div className="flex flex-column gap-2 w-full mb-3">
      <div className="flex justify-content-between align-items-center">
        <h2 className="text-xl font-bold m-0">Orders</h2>
        <Button 
          icon="pi pi-filter" 
          className="p-button-rounded p-button-outlined"
          onClick={() => setFilterSidebarVisible(true)}
          badge={statusFilter || searchTerm || (dateRange && dateRange[0] !== dateRange[1]) ? "1" : null}
          badgeClassName="p-badge-danger"
        />
      </div>
      <div className="flex gap-2">
        <span className="p-input-icon-left flex-1">
          <i className="pi pi-search" />
          <InputText 
            placeholder="Search orders..." 
            className="w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </span>
      </div>
    </div>
  );

  // Desktop header
  const desktopHeader = (
    <div className="flex flex-wrap justify-content-between align-items-center gap-3">
      <h2 className="text-2xl font-bold m-0">Orders</h2>
      <div className="flex gap-2">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText 
            placeholder="Search orders..." 
            className="w-20rem"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </span>
        <Dropdown
          value={statusFilter}
          options={statusOptions}
          onChange={(e) => setStatusFilter(e.value)}
          placeholder="Filter by Status"
          className="w-14rem"
          showClear={statusFilter !== null}
        />
        <Calendar 
          value={dateRange}
          selectionMode="range"
          onChange={(e) => setDateRange(e.value)}
          showIcon
          placeholder="Date Range"
          className="w-18rem"
          readOnlyInput
          showButtonBar
        />
      </div>
    </div>
  );

  // Filter sidebar for mobile
  const filterSidebar = (
    <Sidebar 
      visible={filterSidebarVisible} 
      onHide={() => setFilterSidebarVisible(false)}
      position="right"
      className="w-full md:w-20rem"
    >
      <div className="p-4">
        <h3 className="text-xl font-bold mb-4">Filters</h3>
        
        <div className="mb-4">
          <label className="block font-medium mb-2">Search</label>
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText 
              placeholder="Search orders..." 
              className="w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </span>
        </div>

        <div className="mb-4">
          <label className="block font-medium mb-2">Status</label>
          <Dropdown
            value={statusFilter}
            options={statusOptions}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="Select Status"
            className="w-full"
            showClear={statusFilter !== null}
          />
        </div>

        <div className="mb-4">
          <label className="block font-medium mb-2">Date Range</label>
          <Calendar 
            value={dateRange}
            selectionMode="range"
            onChange={(e) => setDateRange(e.value)}
            showIcon
            placeholder="Select Date Range"
            className="w-full"
            readOnlyInput
            showButtonBar
          />
        </div>

        <div className="flex gap-2 mt-4">
          <Button 
            label="Apply Filters" 
            icon="pi pi-check" 
            className="p-button-success flex-1"
            onClick={applyFilters}
          />
          <Button 
            label="Clear" 
            icon="pi pi-times" 
            className="p-button-outlined p-button-danger"
            onClick={clearFilters}
          />
        </div>

        <div className="mt-4">
          <Button 
            label="Cancel" 
            icon="pi pi-arrow-left" 
            className="p-button-text w-full"
            onClick={() => setFilterSidebarVisible(false)}
          />
        </div>
      </div>
    </Sidebar>
  );

  // Skeleton loader component
  const StatCardSkeleton = () => (
    <Card>
      <div className="flex justify-between items-center">
        <div>
          <Skeleton width="8rem" height="1rem" className="mb-2" />
          <Skeleton width="4rem" height="2rem" />
        </div>
        <Skeleton shape="circle" size="4rem" />
      </div>
      <Skeleton width="100%" height="0.5rem" className="mt-3" />
    </Card>
  );

  const notificationItems = notifications.map(notification => ({
    label: notification.title,
    icon: 'pi pi-bell',
    command: () => handleNotificationClick(notification)
  }));

  return (
    <div className="dashboard-container p-2 md:p-4">
      <Toast ref={toast} />
      <Menu model={notificationItems} popup ref={menu} />
      <ConfirmDialog />
      
      {/* Filter Sidebar for Mobile */}
      {filterSidebar}

      {/* Header */}
      <header className="dashboard-header p-3 md:p-4 bg-blue-600 text-white border-round mb-3">
        <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-bold">Laundry Service (Staff)</h1>
            <p className="text-sm md:text-base text-gray-200">Staff Portal</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Tag value="Driver" className="bg-white text-blue-600" />
              <Tag value={`ID: ${staffId || 'N/A'}`} className="bg-white text-blue-600" />
              <Tag value="Shift: Active" className="bg-white text-green-600" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Badge value={notifications.length} severity="danger">
              <i 
                className="pi pi-bell text-xl md:text-2xl cursor-pointer" 
                onClick={(e) => menu.current.toggle(e)}
              ></i>
            </Badge>
            <Avatar 
              label="S" 
              size="large" 
              shape="circle" 
              className="bg-white text-blue-600 border-2 border-white"
            />
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid mt-2 md:mt-4">
        {loading.stats ? (
          <>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="col-12 md:col-6 lg:col-3">
                <StatCardSkeleton />
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="col-6 md:col-6 lg:col-3">
              <Card className="shadow-1">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block text-xs md:text-sm text-600 font-medium">Today's Orders</span>
                    <div className="text-lg md:text-2xl lg:text-3xl font-bold">{stats.totalOrdersToday}</div>
                  </div>
                  <div className="p-2 md:p-3 bg-blue-100 rounded-full">
                    <i className="pi pi-shopping-cart text-xl md:text-2xl lg:text-3xl text-blue-500"></i>
                  </div>
                </div>
                <ProgressBar value={(stats.totalOrdersToday / 20) * 100} className="mt-2 md:mt-3" />
                <small className="text-500 text-xs">Target: 20</small>
              </Card>
            </div>

            <div className="col-6 md:col-6 lg:col-3">
              <Card className="shadow-1">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block text-xs md:text-sm text-600 font-medium">Pending Pickups</span>
                    <div className="text-lg md:text-2xl lg:text-3xl font-bold">{stats.pendingPickups}</div>
                  </div>
                  <div className="p-2 md:p-3 bg-orange-100 rounded-full">
                    <i className="pi pi-truck text-xl md:text-2xl lg:text-3xl text-orange-500"></i>
                  </div>
                </div>
                <ProgressBar value={(stats.pendingPickups / 10) * 100} className="mt-2 md:mt-3" />
                <small className="text-500 text-xs">Need attention</small>
              </Card>
            </div>

            <div className="col-6 md:col-6 lg:col-3">
              <Card className="shadow-1">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block text-xs md:text-sm text-600 font-medium">Pending Deliveries</span>
                    <div className="text-lg md:text-2xl lg:text-3xl font-bold">{stats.pendingDeliveries}</div>
                  </div>
                  <div className="p-2 md:p-3 bg-green-100 rounded-full">
                    <i className="pi pi-home text-xl md:text-2xl lg:text-3xl text-green-500"></i>
                  </div>
                </div>
                <ProgressBar value={(stats.pendingDeliveries / 10) * 100} className="mt-2 md:mt-3" />
                <small className="text-500 text-xs">Ready to deliver</small>
              </Card>
            </div>

            <div className="col-6 md:col-6 lg:col-3">
              <Card className="shadow-1">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="block text-xs md:text-sm text-600 font-medium">Today's Revenue</span>
                    <div className="text-lg md:text-2xl lg:text-3xl font-bold">${stats.revenueToday?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div className="p-2 md:p-3 bg-purple-100 rounded-full">
                    <i className="pi pi-wallet text-xl md:text-2xl lg:text-3xl text-purple-500"></i>
                  </div>
                </div>
                <ProgressBar value={(stats.revenueToday / 1000) * 100} className="mt-2 md:mt-3" />
                <small className="text-500 text-xs">Target: $1000</small>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Urgent Orders Alert */}
      {stats.urgentOrders > 0 && (
        <Card className="mt-3 md:mt-4 border-red-200 bg-red-50">
          <div className="flex flex-column sm:flex-row justify-between items-start sm:items-center gap-2 md:gap-3">
            <div className="flex items-center gap-2 md:gap-3">
              <i className="pi pi-exclamation-triangle text-red-500 text-xl md:text-2xl"></i>
              <div>
                <h3 className="font-bold text-sm md:text-base text-red-700">Urgent Orders Alert</h3>
                <p className="text-xs md:text-sm text-red-600">You have {stats.urgentOrders} urgent orders</p>
              </div>
            </div>
            <Button 
              label="View Urgent" 
              icon="pi pi-arrow-right" 
              className="p-button-danger p-button-sm w-full sm:w-auto"
              onClick={() => setActiveTab(2)}
            />
          </div>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid mt-3 md:mt-4">
        <div className="col-12 lg:col-8">
          <Card className="shadow-1 h-full">
            {/* Responsive TabView */}
            <TabView 
              activeIndex={activeTab} 
              onTabChange={(e) => setActiveTab(e.index)}
              className={mobileView ? 'mobile-tabview' : ''}
            >
              <TabPanel 
                header={
                  <span className="flex align-items-center gap-2">
                    <i className="pi pi-sync"></i>
                    <span className={mobileView ? 'hidden' : ''}>Active Orders</span>
                  </span>
                }
              >
                {loading.orders ? (
                  <div className="p-2 md:p-4">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} height="4rem" className="mb-2" />
                    ))}
                  </div>
                ) : (
                  <>
                    {mobileView && mobileHeader}
                    {mobileView ? (
                      <div className="p-2">
                        {orders.filter(o => !['Completed', 'Cancelled'].includes(o.status))
                          .map(order => mobileOrderCard(order))}
                      </div>
                    ) : (
                      <div className="datatable-responsive">
                        <DataTable 
                          value={orders.filter(o => !['Completed', 'Cancelled'].includes(o.status))}
                          header={desktopHeader}
                          paginator 
                          rows={5}
                          responsiveLayout="scroll"
                          className="p-datatable-sm"
                          emptyMessage="No active orders found."
                          scrollable
                          scrollHeight="flex"
                        >
                          <Column field="bookingReference" header="Order ID" sortable style={{ minWidth: '100px' }}></Column>
                          <Column field="customerName" header="Customer" sortable style={{ minWidth: '120px' }}></Column>
                          <Column field="address" header="Pickup Address" sortable style={{ minWidth: '150px' }} body={(rowData) => (
                            <span title={rowData.address}>
                              {rowData.address?.length > 20 ? rowData.address.substring(0, 20) + '...' : rowData.address}
                            </span>
                          )}></Column>
                          <Column field="pickupTime" header="Pickup Time" sortable style={{ minWidth: '120px' }}></Column>
                          <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '100px' }}></Column>
                          <Column field="priority" header="Priority" body={priorityBodyTemplate} sortable style={{ minWidth: '90px' }}></Column>
                          <Column field="isUrgent" header="Urgent" body={urgentBodyTemplate} style={{ minWidth: '80px' }}></Column>
                          <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '120px' }}></Column>
                        </DataTable>
                      </div>
                    )}
                  </>
                )}
              </TabPanel>
              
              <TabPanel 
                header={
                  <span className="flex align-items-center gap-2">
                    <i className="pi pi-list"></i>
                    <span className={mobileView ? 'hidden' : ''}>All Orders</span>
                  </span>
                }
              >
                {loading.orders ? (
                  <div className="p-2 md:p-4">
                    {[1, 2, 3, 4].map(i => (
                      <Skeleton key={i} height="4rem" className="mb-2" />
                    ))}
                  </div>
                ) : (
                  <>
                    {mobileView && mobileHeader}
                    {mobileView ? (
                      <div className="p-2">
                        {orders.slice(0, 5).map(order => mobileOrderCard(order))}
                      </div>
                    ) : (
                      <div className="datatable-responsive">
                        <DataTable 
                          value={orders}
                          header={desktopHeader}
                          paginator 
                          rows={8}
                          responsiveLayout="scroll"
                          emptyMessage="No orders found."
                          scrollable
                          scrollHeight="flex"
                        >
                          <Column field="bookingReference" header="Order ID" sortable style={{ minWidth: '100px' }}></Column>
                          <Column field="customerName" header="Customer" sortable style={{ minWidth: '120px' }}></Column>
                          <Column field="serviceType" header="Service" sortable style={{ minWidth: '100px' }}></Column>
                          <Column field="totalAmount" header="Amount" sortable body={(rowData) => `$${rowData.totalAmount?.toFixed(2) || '0.00'}`} style={{ minWidth: '90px' }}></Column>
                          <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '100px' }}></Column>
                          <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '120px' }}></Column>
                        </DataTable>
                      </div>
                    )}
                  </>
                )}
              </TabPanel>
              
              <TabPanel 
                header={
                  <span className="flex align-items-center gap-2">
                    <i className="pi pi-exclamation-triangle"></i>
                    <span className={mobileView ? 'hidden' : ''}>Urgent</span>
                    {urgentOrders.length > 0 && (
                      <Badge value={urgentOrders.length} severity="danger" />
                    )}
                  </span>
                }
              >
                <div className="p-2 md:p-4">
                  {mobileView && (
                    <div className="mb-3">
                      <h3 className="text-lg font-bold text-red-700">Urgent Orders ({urgentOrders.length})</h3>
                    </div>
                  )}
                  {urgentOrders.length > 0 ? (
                    <div className="space-y-3" style={{ maxHeight: mobileView ? '400px' : '500px', overflowY: 'auto' }}>
                      {urgentOrders.map((order) => (
                        <Card key={order.id} className="border-red-200 bg-red-50">
                          <div className="flex flex-column sm:flex-row justify-between items-start gap-2 md:gap-3">
                            <div className="w-full">
                              <div className="flex flex-wrap gap-1 md:gap-2 mb-2">
                                <Tag value="URGENT" severity="danger" />
                                <Tag value={order.priority} severity="danger" />
                              </div>
                              <h4 className="font-bold text-sm md:text-base">{order.bookingReference} - {order.customerName}</h4>
                              <p className="text-xs md:text-sm text-gray-600">{order.address}</p>
                              <p className="text-xs"><strong>Pickup:</strong> {order.pickupTime}</p>
                              <p className="text-xs"><strong>Status:</strong> {order.status}</p>
                            </div>
                            <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                              <Button 
                                label="Action" 
                                icon="pi pi-check" 
                                className="p-button-danger p-button-sm flex-1 sm:flex-none"
                                onClick={() => handleStatusUpdate(order.id, getNextStatus(order.status))}
                              />
                              <Button 
                                label="Details" 
                                icon="pi pi-eye" 
                                className="p-button-outlined p-button-danger p-button-sm flex-1 sm:flex-none"
                                onClick={() => openOrderDetails(order)}
                              />
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center p-4 md:p-8">
                      <i className="pi pi-check-circle text-3xl md:text-5xl text-green-500 mb-3"></i>
                      <h4 className="text-base md:text-xl font-bold text-gray-700">No Urgent Orders</h4>
                      <p className="text-xs md:text-sm text-gray-600">All orders are being handled properly.</p>
                    </div>
                  )}
                </div>
              </TabPanel>
              
              <TabPanel 
                header={
                  <span className="flex align-items-center gap-2">
                    <i className="pi pi-map-marker"></i>
                    <span className={mobileView ? 'hidden' : ''}>Route Map</span>
                  </span>
                }
              >
                <div className="p-2 md:p-4">
                  <h3 className="text-lg md:text-xl font-bold mb-3">Live Staff Locations</h3>
                  {liveLocations.length > 0 ? (
                    <div className="space-y-3" style={{ maxHeight: mobileView ? '400px' : '500px', overflowY: 'auto' }}>
                      {liveLocations.map((staff) => (
                        <Card key={staff.id} className="border-blue-100">
                          <div className="flex flex-column sm:flex-row items-start sm:items-center gap-2 md:gap-3">
                            <Avatar 
                              label={staff.fullName?.charAt(0) || "U"} 
                              size="large" 
                              shape="circle"
                              className="bg-blue-100 text-blue-600"
                            />
                            <div className="flex-1 w-full">
                              <h4 className="font-bold text-sm md:text-base">{staff.fullName}</h4>
                              <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                                <span>Status: <Tag value={staff.availabilityStatus} severity={staff.availabilityStatus === 'Available' ? 'success' : 'warning'} /></span>
                                {staff.vehicleNumber && <span>Vehicle: {staff.vehicleNumber}</span>}
                              </div>
                            </div>
                            {staff.currentLatitude && staff.currentLongitude && (
                              <Button 
                                icon="pi pi-map-marker" 
                                label="View" 
                                className="p-button-outlined p-button-sm w-full sm:w-auto"
                                onClick={() => window.open(`https://maps.google.com/?q=${staff.currentLatitude},${staff.currentLongitude}`, '_blank')}
                              />
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center p-4 md:p-8">
                      <i className="pi pi-map-marker text-3xl md:text-5xl text-gray-300 mb-3"></i>
                      <h4 className="text-base md:text-xl font-bold text-gray-700">No Active Staff</h4>
                      <p className="text-xs md:text-sm text-gray-600">No staff members are currently active.</p>
                    </div>
                  )}
                </div>
              </TabPanel>
            </TabView>
          </Card>
        </div>

        <div className="col-12 lg:col-4">
          <div className="grid">
            <div className="col-12">
              <Card title="Weekly Performance" className="shadow-1">
                {loading.chart ? (
                  <div style={{ height: mobileView ? '200px' : '250px' }} className="flex align-items-center justify-content-center">
                    <Skeleton width="90%" height="90%" />
                  </div>
                ) : (
                  <div style={{ height: mobileView ? '200px' : '250px' }}>
                    <Chart type="line" data={chartData} options={chartOptions} />
                  </div>
                )}
              </Card>
            </div>

            <div className="col-12">
              <Card title="Quick Actions" className="shadow-1 mt-3 md:mt-4">
                <div className="grid">
                  <div className="col-6 md:col-12">
                    <Button 
                      label="Start Shift" 
                      icon="pi pi-play" 
                      className="p-button-success w-full mb-2 p-button-sm md:p-button-md" 
                      onClick={handleStartShift}
                    />
                  </div>
                  <div className="col-6 md:col-12">
                    <Button 
                      label="End Shift" 
                      icon="pi pi-stop" 
                      className="p-button-danger w-full mb-2 p-button-sm md:p-button-md" 
                      onClick={handleEndShift}
                    />
                  </div>
                  <div className="col-6">
                    <Button 
                      label="Pickup" 
                      icon="pi pi-check" 
                      className="p-button-info w-full mb-2 p-button-sm" 
                      onClick={() => showToast('info', 'Info', 'Select an order')}
                    />
                  </div>
                  <div className="col-6">
                    <Button 
                      label="Delivery" 
                      icon="pi pi-home" 
                      className="p-button-help w-full mb-2 p-button-sm" 
                      onClick={() => showToast('info', 'Info', 'Select an order')}
                    />
                  </div>
                  <div className="col-12">
                    <Button 
                      label="Report Issue" 
                      icon="pi pi-exclamation-triangle" 
                      className="p-button-warning w-full p-button-sm" 
                      onClick={() => showToast('warn', 'Warning', 'Report issue')}
                    />
                  </div>
                </div>
              </Card>
            </div>

            <div className="col-12">
              <Card title="Today's Schedule" className="shadow-1 mt-3 md:mt-4">
                <div style={{ maxHeight: mobileView ? '300px' : '400px', overflowY: 'auto' }}>
                  <ul className="p-0 m-0 list-none">
                    {orders.slice(0, 4).map((order) => (
                      <li key={order.id} className="flex align-items-center py-2 md:py-3 border-bottom-1 surface-border">
                        <div className={`w-2rem h-2rem md:w-3rem md:h-3rem flex align-items-center justify-content-center border-circle mr-2 md:mr-3 flex-shrink-0 ${
                          order.status === 'Pending' ? 'bg-yellow-100' :
                          order.status === 'PickedUp' ? 'bg-green-100' :
                          order.status === 'OutForDelivery' ? 'bg-blue-100' : 'bg-purple-100'
                        }`}>
                          <i className={`pi text-sm md:text-base ${
                            order.status === 'Pending' ? 'pi-clock text-yellow-500' :
                            order.status === 'PickedUp' ? 'pi-check text-green-500' :
                            order.status === 'OutForDelivery' ? 'pi-truck text-blue-500' : 'pi-home text-purple-500'
                          }`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-bold text-sm md:text-base block truncate">{order.customerName}</span>
                          <div className="text-xs md:text-sm text-600">{order.serviceType} • {order.pickupTime}</div>
                        </div>
                        <div className="flex-shrink-0 ml-1 md:ml-2">
                          <Tag 
                            value={order.status} 
                            severity={statusSeverity[order.status] || 'info'} 
                            className="text-xs"
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Order Detail Dialog */}
      <Dialog 
        header={`Order Details - ${orderDetails?.order?.bookingReference}`} 
        visible={orderDialog} 
        style={{ width: mobileView ? '95vw' : '70vw', maxWidth: '1200px' }}
        onHide={() => {
          setOrderDialog(false);
          setOrderDetails(null);
        }}
        maximizable
      >
        {orderDetails && (
          <div className="grid">
            {/* Customer Information */}
            <div className="col-12 md:col-6">
              <h4 className="font-bold text-sm md:text-base mb-2">Customer Information</h4>
              <p className="text-sm"><strong>Name:</strong> {orderDetails.order.customerName}</p>
              <p className="text-sm"><strong>Phone:</strong> {orderDetails.order.customerPhone}</p>
              <p className="text-sm"><strong>Address:</strong> {orderDetails.order.address}</p>
              <Button 
                label="Get Directions" 
                icon="pi pi-directions" 
                className="p-button-sm p-button-outlined mt-2"
                onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(orderDetails.order.address)}`, '_blank')}
              />
            </div>

            {/* Order Details */}
            <div className="col-12 md:col-6">
              <h4 className="font-bold text-sm md:text-base mb-2">Order Details</h4>
              <p className="text-sm"><strong>Order ID:</strong> {orderDetails.order.bookingReference}</p>
              <p className="text-sm"><strong>Service:</strong> {orderDetails.order.serviceType}</p>
              <p className="text-sm"><strong>Amount:</strong> ${orderDetails.order.totalAmount?.toFixed(2) || '0.00'}</p>
              <p className="text-sm"><strong>Priority:</strong> <Tag value={orderDetails.order.priority} severity={
                orderDetails.order.priority === 'Urgent' ? 'danger' :
                orderDetails.order.priority === 'High' ? 'warning' :
                orderDetails.order.priority === 'Medium' ? 'info' : 'success'
              } /></p>
              <p className="text-sm"><strong>Status:</strong> <Tag value={orderDetails.order.status} severity={statusSeverity[orderDetails.order.status] || 'info'} /></p>
              {orderDetails.order.isUrgent && (
                <p className="text-sm mt-2"><Tag value="URGENT" severity="danger" /></p>
              )}
            </div>

            {/* Schedule */}
            <div className="col-12">
              <Divider />
              <h4 className="font-bold text-sm md:text-base mb-2">Schedule</h4>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <p className="text-sm">
                    <strong>Pickup:</strong> {orderDetails.order.pickupDate ? new Date(orderDetails.order.pickupDate).toLocaleDateString() : 'N/A'} 
                    {orderDetails.order.pickupTime !== 'Not specified' && ` at ${orderDetails.order.pickupTime}`}
                  </p>
                </div>
                <div className="col-12 md:col-6">
                  <p className="text-sm">
                    <strong>Delivery:</strong> {orderDetails.order.deliveryDate ? new Date(orderDetails.order.deliveryDate).toLocaleDateString() : 'N/A'} 
                    {orderDetails.order.deliveryTime !== 'Not specified' && ` at ${orderDetails.order.deliveryTime}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Staff Assignments */}
            {orderDetails.staffAssignments && orderDetails.staffAssignments.length > 0 && (
              <div className="col-12">
                <Divider />
                <h4 className="font-bold text-sm md:text-base mb-2">Staff Assignments</h4>
                <div className="grid">
                  {orderDetails.staffAssignments.map((assignment, index) => (
                    <div key={index} className="col-12 md:col-6">
                      <Card className="bg-gray-50">
                        <p className="text-sm font-bold capitalize">{assignment.assignmentType} Staff</p>
                        <p className="text-sm"><strong>Name:</strong> {assignment.staffName}</p>
                        <p className="text-sm"><strong>Phone:</strong> {assignment.staffPhone}</p>
                        <p className="text-sm"><strong>Status:</strong> <Tag value={assignment.status} severity={
                          assignment.status === 'Completed' ? 'success' :
                          assignment.status === 'Assigned' ? 'info' : 'warning'
                        } /></p>
                        <p className="text-sm"><strong>Assigned:</strong> {assignment.assignedDate ? new Date(assignment.assignedDate).toLocaleString() : 'N/A'}</p>
                        {assignment.startedAt && (
                          <p className="text-sm"><strong>Started:</strong> {new Date(assignment.startedAt).toLocaleString()}</p>
                        )}
                        {assignment.completedAt && (
                          <p className="text-sm"><strong>Completed:</strong> {new Date(assignment.completedAt).toLocaleString()}</p>
                        )}
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items */}
            {orderDetails.items && orderDetails.items.length > 0 && (
              <div className="col-12">
                <Divider />
                <h4 className="font-bold text-sm md:text-base mb-2">Order Items</h4>
                <DataTable value={orderDetails.items} size="small" className="p-datatable-sm">
                  <Column field="itemType" header="Item"></Column>
                  <Column field="quantity" header="Qty"></Column>
                  <Column field="treatment" header="Treatment"></Column>
                  <Column field="unitPrice" header="Unit Price" body={(rowData) => `$${rowData.unitPrice?.toFixed(2) || '0.00'}`}></Column>
                  <Column field="totalPrice" header="Total" body={(rowData) => `$${rowData.totalPrice?.toFixed(2) || '0.00'}`}></Column>
                </DataTable>
              </div>
            )}

            {/* Special Instructions */}
            {orderDetails.order.specialInstructions && (
              <div className="col-12">
                <Divider />
                <h4 className="font-bold text-sm md:text-base mb-2">Special Instructions</h4>
                <div className="p-3 bg-gray-100 border-round text-sm">
                  {orderDetails.order.specialInstructions}
                </div>
              </div>
            )}

            {/* Status History */}
            {orderDetails.statusHistory && orderDetails.statusHistory.length > 0 && (
              <div className="col-12">
                <Divider />
                <h4 className="font-bold text-sm md:text-base mb-2">Status History</h4>
                <Timeline 
                  value={[...orderDetails.statusHistory].reverse()}
                  content={(item) => (
                    <div className="p-2">
                      <p className="text-sm font-bold">{item.newStatus}</p>
                      <p className="text-xs text-gray-600">
                        {new Date(item.changedAt).toLocaleString()} 
                        {item.changedBy && ` by ${item.changedBy}`}
                      </p>
                      {item.changeReason && (
                        <p className="text-xs mt-1"><strong>Reason:</strong> {item.changeReason}</p>
                      )}
                      {item.notes && (
                        <p className="text-xs"><strong>Notes:</strong> {item.notes}</p>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {/* Payment Information */}
            {orderDetails.payment && (
              <div className="col-12">
                <Divider />
                <h4 className="font-bold text-sm md:text-base mb-2">Payment Information</h4>
                <Card className="bg-gray-50">
                  <p className="text-sm"><strong>Transaction ID:</strong> {orderDetails.payment.transactionId || 'N/A'}</p>
                  <p className="text-sm"><strong>Amount:</strong> ${orderDetails.payment.amount?.toFixed(2) || '0.00'}</p>
                  <p className="text-sm"><strong>Method:</strong> {orderDetails.payment.paymentMethod}</p>
                  <p className="text-sm"><strong>Status:</strong> <Tag value={orderDetails.payment.status} severity={
                    orderDetails.payment.status === 'Completed' ? 'success' :
                    orderDetails.payment.status === 'Pending' ? 'warning' : 'danger'
                  } /></p>
                  <p className="text-sm"><strong>Date:</strong> {orderDetails.payment.paymentDate ? new Date(orderDetails.payment.paymentDate).toLocaleString() : 'N/A'}</p>
                </Card>
              </div>
            )}
          </div>
        )}
      </Dialog>

      <style>{`
        .datatable-responsive {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        
        .datatable-responsive :global(.p-datatable) {
          min-width: 800px;
        }
        
        /* Mobile TabView Styles */
        :global(.mobile-tabview .p-tabview-nav) {
          display: flex;
          flex-wrap: nowrap;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          padding-bottom: 2px;
        }
        
        :global(.mobile-tabview .p-tabview-nav::-webkit-scrollbar) {
          display: none;
        }
        
        :global(.mobile-tabview .p-tabview-nav li) {
          flex: 0 0 auto;
          margin-right: 0.25rem;
        }
        
        :global(.mobile-tabview .p-tabview-nav .p-tabview-nav-link) {
          padding: 0.75rem 1rem;
        }
        
        :global(.mobile-tabview .p-tabview-panels) {
          padding: 1rem 0.5rem;
        }
        
        .hidden {
          display: none;
        }
        
        @media screen and (max-width: 768px) {
          .dashboard-container {
            padding: 0.25rem;
          }
          
          .datatable-responsive :global(.p-datatable) {
            min-width: 600px;
          }
          
          :global(.p-tabview-panels) {
            padding: 0.75rem 0.5rem !important;
          }
          
          :global(.p-card-body) {
            padding: 0.75rem !important;
          }
          
          :global(.p-card-title) {
            font-size: 1rem !important;
            margin-bottom: 0.5rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StaffDashboard;